import React, { createContext, useCallback, useContext, useState } from 'react';
import { Alert, type AlertColor, Snackbar } from '@mui/material';

interface Notification {
  id: number;
  message: string;
  severity: AlertColor;
  duration: number;
}

interface NotificationContextValue {
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

let nextId = 0;

const DEFAULT_DURATIONS: Record<AlertColor, number> = {
  success: 4000,
  error: 6000,
  warning: 5000,
  info: 4000,
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notification, setNotification] = useState<Notification | null>(null);

  const show = useCallback((message: string, severity: AlertColor, duration?: number) => {
    setNotification({
      id: ++nextId,
      message,
      severity,
      duration: duration ?? DEFAULT_DURATIONS[severity],
    });
  }, []);

  const handleClose = useCallback((_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setNotification(null);
  }, []);

  const value: NotificationContextValue = {
    showSuccess: useCallback((msg, dur?) => show(msg, 'success', dur), [show]),
    showError: useCallback((msg, dur?) => show(msg, 'error', dur), [show]),
    showWarning: useCallback((msg, dur?) => show(msg, 'warning', dur), [show]),
    showInfo: useCallback((msg, dur?) => show(msg, 'info', dur), [show]),
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Snackbar
        key={notification?.id}
        open={notification !== null}
        autoHideDuration={notification?.duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {notification ? (
          <Alert onClose={handleClose} severity={notification.severity} sx={{ width: '100%' }}>
            {notification.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </NotificationContext.Provider>
  );
};

export function useNotification(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return ctx;
}
